import React, { useState, useEffect } from 'react';
import { Navigate, Link, useSearchParams } from 'react-router-dom';
import { SEOHead, seoConfigs } from '@/components/SEOHead';
import Navigation from '@/components/Navigation';
import { useAuth } from '@/components/AuthProvider';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Mail, Lock, User, ArrowLeft, Loader2, Linkedin } from 'lucide-react';
import UnifiedLoader from '@/components/ui/UnifiedLoader';
import { toast } from '@/hooks/use-toast';
import { isInIframe, openOAuthUrl } from '@/lib/oauth';
import HeroBackground from '@/components/HeroBackground';
import chopperSwapLogo from '@/assets/chopperswap-logo-new.png';
import { PasswordStrengthIndicator, getPasswordStrength } from '@/components/auth/PasswordStrengthIndicator';
import { EmailConfirmationScreen } from '@/components/auth/EmailConfirmationScreen';
import { AuthInstallPrompt } from '@/components/pwa/AuthInstallPrompt';
import { Checkbox } from '@/components/ui/checkbox';

const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

const Auth = () => {
  const { user, signIn, signUp, resendConfirmationEmail, loading } = useAuth();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  // Initialize activeTab from URL parameter (mode=signup opens Sign Up tab)
  const [activeTab, setActiveTab] = useState<'signin' | 'signup'>(() => {
    const mode = searchParams.get('mode');
    return mode === 'signup' ? 'signup' : 'signin';
  });
  const [showConfirmationScreen, setShowConfirmationScreen] = useState(false);
  const [confirmationEmail, setConfirmationEmail] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  // Get redirect destination from URL params
  const redirectTo = searchParams.get('redirect') 
    ? decodeURIComponent(searchParams.get('redirect')!) 
    : '/';

  // Store redirect before OAuth (since we lose URL params after OAuth redirect)
  useEffect(() => {
    if (redirectTo && redirectTo !== '/') {
      localStorage.setItem('authRedirect', redirectTo);
    }
  }, [redirectTo]);

  // Poll for OAuth completion in iframe contexts
  useEffect(() => {
    if (!isInIframe()) return;
    
    const interval = setInterval(async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        // Session detected from OAuth popup — force state refresh
        window.location.reload();
      }
    }, 2000);

    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return <UnifiedLoader mode="fullscreen" text="Checking authentication" />;
  }

  // Redirect authenticated users to their intended destination
  if (user) {
    // Check for stored redirect (from OAuth flow)
    const storedRedirect = localStorage.getItem('authRedirect');
    if (storedRedirect) {
      localStorage.removeItem('authRedirect');
      return <Navigate to={storedRedirect} replace />;
    }
    return <Navigate to={redirectTo} replace />;
  }

  const handleResendFromSignIn = async () => {
    const { error } = await resendConfirmationEmail(email);
    if (error) {
      toast({
        title: "Failed to Resend",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Email Sent!",
        description: "Check your inbox for the verification link.",
      });
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    
    const { error } = await signIn(email, password);
    
    if (error) {
      // Check for unverified email error
      if (error.message.toLowerCase().includes('email not confirmed')) {
        toast({
          title: "Email Not Verified",
          description: "Please verify your email before signing in.",
          variant: "destructive",
          action: (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleResendFromSignIn}
              className="border-white/30 text-white hover:bg-white/10"
            >
              Resend Email
            </Button>
          ),
        });
      } else {
        toast({
          title: "Sign In Failed",
          description: error.message,
          variant: "destructive",
        });
      }
    } else {
      toast({
        title: "Welcome back!",
        description: "You've been signed in successfully.",
      });
    }
    
    setIsLoading(false);
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    
    // Validate password strength (require at least "Fair" - score >= 3)
    const passwordStrength = getPasswordStrength(password);
    if (passwordStrength.score < 3) {
      toast({
        title: "Password Too Weak",
        description: "Please create a stronger password with at least 8 characters, including uppercase, lowercase, and numbers.",
        variant: "destructive",
      });
      setIsLoading(false);
      return;
    }

    // Validate passwords match
    if (password !== confirmPassword) {
      toast({
        title: "Passwords Don't Match",
        description: "Please make sure both passwords are the same.",
        variant: "destructive",
      });
      setIsLoading(false);
      return;
    }
    
    const { error, isExistingUser } = await signUp(email, password, displayName);
    
    if (error) {
      if (isExistingUser) {
        // Show helpful message for existing users
        toast({
          title: "Email Already Registered",
          description: "This email is already in use. Try signing in or reset your password.",
          variant: "destructive",
          action: (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => {
                setActiveTab('signin');
                setShowForgotPassword(false);
              }}
              className="border-white/30 text-white hover:bg-white/10"
            >
              Sign In
            </Button>
          ),
        });
      } else {
        toast({
          title: "Sign Up Failed",
          description: error.message,
          variant: "destructive",
        });
      }
    } else {
      // Show confirmation screen
      setConfirmationEmail(email);
      setShowConfirmationScreen(true);
    }
    
    setIsLoading(false);
  };

  const handleBackToSignIn = () => {
    setShowConfirmationScreen(false);
    setActiveTab('signin');
    // Clear signup form
    setPassword('');
    setConfirmPassword('');
    setDisplayName('');
  };

  const handleGoogleSignIn = async () => {
    setIsLoading(true);
    try {
      const inIframe = isInIframe();
      
      // Store redirect before OAuth (will be read after callback)
      if (redirectTo && redirectTo !== '/') {
        localStorage.setItem('authRedirect', redirectTo);
      }
      
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          // Always redirect to /auth so we can handle the stored redirect
          redirectTo: `${window.location.origin}/auth`,
          skipBrowserRedirect: inIframe,
        },
      });
      if (error) throw error;
      
      if (inIframe && data?.url) {
        const opened = openOAuthUrl(data.url);
        if (!opened) {
          toast({
            title: "Popup Blocked",
            description: "Please allow popups for this site, then try again.",
            variant: "destructive",
          });
        } else {
          toast({
            title: "Complete Sign In",
            description: "Finish signing in with Google in the new tab. You'll be redirected automatically.",
          });
          // Listen for tab refocus to check if session was established
          const handleVisibility = async () => {
            if (document.visibilityState === 'visible') {
              const { data: { session } } = await supabase.auth.getSession();
              if (session) {
                document.removeEventListener('visibilitychange', handleVisibility);
                window.location.reload();
              }
            }
          };
          document.addEventListener('visibilitychange', handleVisibility);
        }
        setIsLoading(false);
      }
    } catch (error: any) {
      toast({
        title: "Google Sign In Failed",
        description: error.message || "Failed to sign in with Google",
        variant: "destructive",
      });
      setIsLoading(false);
    }
  };

  const handleLinkedInSignIn = async () => {
    setIsLoading(true);
    try {
      const inIframe = isInIframe();
      
      // Store redirect before OAuth (will be read after callback)
      if (redirectTo && redirectTo !== '/') {
        localStorage.setItem('authRedirect', redirectTo);
      }
      
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'linkedin_oidc',
        options: {
          // Always redirect to /auth so we can handle the stored redirect
          redirectTo: `${window.location.origin}/auth`,
          scopes: 'openid profile email',
          skipBrowserRedirect: inIframe,
        },
      });
      if (error) throw error;
      
      if (inIframe && data?.url) {
        const opened = openOAuthUrl(data.url);
        if (!opened) {
          toast({
            title: "Popup Blocked",
            description: "Please allow popups for this site, then try again.",
            variant: "destructive",
          });
        } else {
          toast({
            title: "Complete Sign In",
            description: "Finish signing in with LinkedIn in the new tab. You'll be redirected automatically.",
          });
          const handleVisibility = async () => {
            if (document.visibilityState === 'visible') {
              const { data: { session } } = await supabase.auth.getSession();
              if (session) {
                document.removeEventListener('visibilitychange', handleVisibility);
                window.location.reload();
              }
            }
          };
          document.addEventListener('visibilitychange', handleVisibility);
        }
        setIsLoading(false);
      }
    } catch (error: any) {
      toast({
        title: "LinkedIn Sign In Failed",
        description: error.message || "Failed to sign in with LinkedIn",
        variant: "destructive",
      });
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      toast({
        title: "Email required",
        description: "Please enter your email address",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) throw error;

      setResetEmailSent(true);
      toast({
        title: "Check your email",
        description: "We sent you a password reset link.",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to send reset email",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleTabChange = (value: string) => {
    if (value === 'signin' || value === 'signup') {
      setActiveTab(value);
      setShowForgotPassword(false);
      setResetEmailSent(false);
      setShowConfirmationScreen(false);
    }
  };

  return (
    <>
      <SEOHead {...seoConfigs.auth} />
      <div className="min-h-screen relative">
        {/* Shared hangar hero background — works in both light and dark mode */}
        <HeroBackground darkScrim />

        <Navigation />
      
      {/* Subtle Back Navigation - More compact on mobile */}
      <div className="relative z-10 pt-[calc(var(--nav-height-mobile)+1rem)] md:pt-[calc(var(--nav-height-desktop)+1rem)] pb-2 md:pb-4">
        <div className="container mx-auto px-4">
          <Link 
            to="/" 
            className="inline-flex items-center text-white/70 hover:text-white dark:text-muted-foreground dark:hover:text-foreground transition-colors text-sm"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            <span className="hidden sm:inline">Back to ChopperSwap</span>
            <span className="sm:hidden">Back</span>
          </Link>
        </div>
      </div>
      
      <div className="relative min-h-[100dvh] flex flex-col items-center justify-center p-3 md:p-4 -mt-16 md:-mt-24 pt-4 md:pt-24 pb-24 md:pb-safe">
        <Card className="force-light-theme auth-light-card w-full max-w-md mx-auto max-h-[calc(100vh-10rem)] md:max-h-none overflow-y-auto bg-card text-card-foreground">
          <CardHeader className="text-center px-3 md:px-6 pt-4 md:pt-6 pb-2 md:pb-4">
            <CardTitle className="text-lg md:text-2xl font-bold text-primary mb-2 md:mb-6">Welcome to ChopperSwap</CardTitle>
            <div className="flex justify-center mb-2 md:mb-6 relative">
              {/* IMPORTANT: mobile dark-mode keeps the logo BLACK (brightness-0 only, NO invert).
                  Only md+ inverts to white. Do NOT add dark:invert to the mobile class — user requested black on phone. */}
              <img 
                src={chopperSwapLogo} 
                alt="ChopperSwap Logo"
                className="h-16 w-16 md:h-40 md:w-40"
              />
              {/* Animated Arrow Overlay - Hidden on mobile for cleaner look */}
              <div className="hidden md:block absolute inset-0 h-40 w-40 mx-auto animate-circular-flow opacity-60">
                <div className="relative w-full h-full">
                  <div className="absolute top-1/4 left-1/2 w-2 h-2 bg-primary rounded-full transform -translate-x-1/2 animate-pulse"></div>
                  <div className="absolute top-1/2 right-1/4 w-2 h-2 bg-primary rounded-full animate-pulse delay-1000"></div>
                  <div className="absolute bottom-1/4 left-1/2 w-2 h-2 bg-primary rounded-full transform -translate-x-1/2 animate-pulse delay-2000"></div>
                  <div className="absolute top-1/2 left-1/4 w-2 h-2 bg-primary rounded-full animate-pulse delay-3000"></div>
                </div>
              </div>
              {/* Rotor Animation Overlay - Hidden on mobile */}
              <div className="hidden md:block absolute top-1/4 left-1/2 transform -translate-x-1/2 w-20 h-20 animate-rotor-spin opacity-40">
                <div className="absolute inset-0 border-t-2 border-primary rounded-full"></div>
                <div className="absolute inset-0 border-b-2 border-primary rounded-full rotate-90"></div>
              </div>
            </div>
            <CardDescription className="text-primary text-sm md:text-lg font-semibold">
              The Professional Helicopter Exchange Platform
            </CardDescription>
          </CardHeader>
        <CardContent className="px-4 md:px-6 pb-6">
          {showConfirmationScreen ? (
            <EmailConfirmationScreen 
              email={confirmationEmail}
              onBackToSignIn={handleBackToSignIn}
            />
          ) : (
          <Tabs 
            value={showForgotPassword ? "forgot" : activeTab} 
            onValueChange={handleTabChange}
            className="w-full"
          >
            <TabsList className="grid w-full grid-cols-2 gap-2 bg-primary/10 backdrop-blur-sm h-12 md:h-11 border border-primary/20 rounded-lg p-1 overflow-visible">
              <TabsTrigger 
                value="signin" 
                disabled={showForgotPassword}
                className="min-w-0 text-primary/80 font-semibold data-[state=active]:bg-primary data-[state=active]:text-white data-[state=inactive]:hover:text-primary data-[state=inactive]:hover:bg-primary/10 h-9 md:h-8 text-sm transition-all rounded-md"
              >
                Sign In
              </TabsTrigger>
              <TabsTrigger 
                value="signup" 
                disabled={showForgotPassword}
                className="min-w-0 text-primary/80 font-semibold data-[state=active]:bg-primary data-[state=active]:text-white data-[state=inactive]:hover:text-primary data-[state=inactive]:hover:bg-primary/10 h-9 md:h-8 text-sm transition-all rounded-md"
              >
                Sign Up
              </TabsTrigger>
            </TabsList>
            
            {showForgotPassword ? (
              <TabsContent value="forgot" className="space-y-6 mt-6">
                {resetEmailSent ? (
                  <div className="text-center space-y-4 py-8">
                    <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                      <Mail className="h-8 w-8 text-green-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-primary">Check your email</h3>
                    <p className="text-primary/80">
                      We've sent a password reset link to <strong>{email}</strong>
                    </p>
                    <Button 
                      variant="outline" 
                      onClick={() => {
                        setShowForgotPassword(false);
                        setResetEmailSent(false);
                      }}
                      className="mt-4 bg-primary/10 border-primary/30 text-primary hover:bg-primary/20"
                    >
                      Back to Sign In
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleForgotPassword} className="space-y-6">
                    <div className="space-y-2 text-center">
                      <h3 className="text-xl font-semibold text-primary">Reset your password</h3>
                      <p className="text-sm text-primary/70">
                        Enter your email and we'll send you a reset link
                      </p>
                    </div>
                    <div className="relative">
                      <Mail className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-primary z-10" />
                      <Input
                        type="email"
                        placeholder="your.email@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        className="pl-10 bg-background/60 border-border placeholder:text-muted-foreground text-foreground backdrop-blur-sm focus:bg-background/80 focus:border-primary focus:ring-1 focus:ring-primary/30"
                      />
                    </div>
                    <div className="space-y-3">
                      <Button 
                        type="submit" 
                        className="w-full font-semibold shadow-lg"
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Sending...
                          </>
                        ) : (
                          "Send Reset Link"
                        )}
                      </Button>
                      <Button 
                        type="button"
                        variant="ghost" 
                        className="w-full text-primary hover:bg-primary/10" 
                        onClick={() => setShowForgotPassword(false)}
                      >
                        Back to Sign In
                      </Button>
                    </div>
                  </form>
                )}
              </TabsContent>
            ) : (
              <>
                <TabsContent value="signin">
                  <form onSubmit={handleSignIn} className="space-y-3 md:space-y-4">
                    <div className="space-y-2 md:space-y-2">
                      <div className="relative">
                        <Mail className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-primary z-10" />
                        <Input
                          type="email"
                          placeholder="Email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="pl-10 h-12 md:h-10 bg-background/60 border-border placeholder:text-muted-foreground text-foreground backdrop-blur-sm focus:bg-background/80 focus:border-primary focus:ring-1 focus:ring-primary/30 text-base md:text-sm"
                          required
                        />
                      </div>
                      <div className="relative">
                        <Lock className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-primary z-10" />
                        <Input
                          type="password"
                          placeholder="Password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className="pl-10 h-12 md:h-10 bg-background/60 border-border placeholder:text-muted-foreground text-foreground backdrop-blur-sm focus:bg-background/80 focus:border-primary focus:ring-1 focus:ring-primary/30 text-base md:text-sm"
                          required
                        />
                      </div>
                    </div>
                    <Button type="submit" className="w-full h-12 md:h-10 text-base md:text-sm font-bold shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30 transition-all duration-200" disabled={isLoading}>
                      {isLoading ? "Signing In..." : "Sign In"}
                    </Button>
                    
                    <div className="relative my-4">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-border"></div>
                      </div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-card/80 px-4 py-1 rounded-full text-muted-foreground text-xs font-medium tracking-wide">OR</span>
                      </div>
                    </div>
                    
                    <Button 
                      type="button"
                      variant="outline"
                      className="w-full h-12 md:h-10 bg-card hover:bg-muted text-foreground font-medium shadow-md hover:shadow-lg transition-all duration-200 border-0 text-base md:text-sm"
                      onClick={handleGoogleSignIn}
                      disabled={isLoading}
                    >
                      <GoogleIcon />
                      <span className="ml-2">Continue with Google</span>
                    </Button>

                    <Button 
                      type="button"
                      variant="outline"
                      className="w-full h-12 md:h-10 bg-[#0077B5] hover:bg-[#005E8C] text-white font-medium shadow-md hover:shadow-lg transition-all duration-200 border-0 text-base md:text-sm"
                      onClick={handleLinkedInSignIn}
                      disabled={isLoading}
                    >
                      <Linkedin className="h-5 w-5" />
                      <span className="ml-2">Continue with LinkedIn</span>
                    </Button>
                    
                    <Button 
                      type="button"
                      variant="link" 
                      className="w-full text-sm text-primary/80 hover:text-primary hover:underline underline-offset-2 h-10" 
                      onClick={() => setShowForgotPassword(true)}
                    >
                      Forgot your password?
                    </Button>
                  </form>
                </TabsContent>
              </>
            )}
            
            <TabsContent value="signup">
              <form onSubmit={handleSignUp} className="space-y-3 md:space-y-4">
                <div className="space-y-2">
                  <div className="relative">
                    <User className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-primary z-10" />
                    <Input
                      type="text"
                      placeholder="Display Name"
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      className="pl-10 h-12 md:h-10 bg-background/60 border-border placeholder:text-muted-foreground text-foreground backdrop-blur-sm focus:bg-background/80 focus:border-primary focus:ring-1 focus:ring-primary/30 text-base md:text-sm"
                    />
                  </div>
                  <div className="relative">
                    <Mail className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-primary z-10" />
                    <Input
                      type="email"
                      placeholder="Email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10 h-12 md:h-10 bg-background/60 border-border placeholder:text-muted-foreground text-foreground backdrop-blur-sm focus:bg-background/80 focus:border-primary focus:ring-1 focus:ring-primary/30 text-base md:text-sm"
                      required
                    />
                  </div>
                  <div className="relative">
                    <Lock className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-primary z-10" />
                    <Input
                      type="password"
                      placeholder="Password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10 h-12 md:h-10 bg-background/60 border-border placeholder:text-muted-foreground text-foreground backdrop-blur-sm focus:bg-background/80 focus:border-primary focus:ring-1 focus:ring-primary/30 text-base md:text-sm"
                      minLength={6}
                      required
                    />
                  </div>
                  {/* Password Strength Indicator */}
                  <PasswordStrengthIndicator password={password} />
                  
                  <div className="relative">
                    <Lock className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-primary z-10" />
                    <Input
                      type="password"
                      placeholder="Confirm Password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="pl-10 h-12 md:h-10 bg-background/60 border-border placeholder:text-muted-foreground text-foreground backdrop-blur-sm focus:bg-background/80 focus:border-primary focus:ring-1 focus:ring-primary/30 text-base md:text-sm"
                      minLength={6}
                      required
                    />
                  </div>
                </div>

                {/* Terms of Service Agreement */}
                <div className="flex items-start gap-2 pt-1">
                  <div className="flex h-5 items-center shrink-0">
                    <Checkbox
                      id="terms-agreement"
                      checked={agreedToTerms}
                      onCheckedChange={(checked) => setAgreedToTerms(checked === true)}
                      className="!h-4 !w-4 !min-h-4 !min-w-4 !max-h-4 !max-w-4 shrink-0 rounded-[4px] aspect-square"
                    />
                  </div>
                  <label htmlFor="terms-agreement" className="text-xs text-muted-foreground leading-relaxed cursor-pointer">
                    I agree to the{' '}
                    <Link to="/terms" className="text-primary underline underline-offset-2 hover:text-primary/80" target="_blank">
                      Terms of Service
                    </Link>{' '}
                    and{' '}
                    <Link to="/privacy" className="text-primary underline underline-offset-2 hover:text-primary/80" target="_blank">
                      Privacy Policy
                    </Link>
                  </label>
                </div>


                <Button type="submit" className="w-full h-12 md:h-10 text-base md:text-sm font-bold shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30 transition-all duration-200" disabled={isLoading || !agreedToTerms}>
                  {isLoading ? "Creating Account..." : "Create Account"}
                </Button>
                
                <div className="relative my-4">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-border"></div>
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-card/80 px-4 py-1 rounded-full text-muted-foreground text-xs font-medium tracking-wide">OR</span>
                  </div>
                </div>
                
                <Button 
                  type="button"
                  variant="outline"
                  className="w-full h-12 md:h-10 bg-card hover:bg-muted text-foreground font-medium shadow-md hover:shadow-lg transition-all duration-200 border-0 text-base md:text-sm"
                  onClick={handleGoogleSignIn}
                  disabled={isLoading}
                >
                  <GoogleIcon />
                  <span className="ml-2">Continue with Google</span>
                </Button>

                <Button 
                  type="button"
                  variant="outline"
                  className="w-full h-12 md:h-10 bg-[#0077B5] hover:bg-[#005E8C] text-white font-medium shadow-md hover:shadow-lg transition-all duration-200 border-0 text-base md:text-sm"
                  onClick={handleLinkedInSignIn}
                  disabled={isLoading}
                >
                  <Linkedin className="h-5 w-5" />
                  <span className="ml-2">Continue with LinkedIn</span>
                </Button>
              </form>
            </TabsContent>
          </Tabs>
          )}
        </CardContent>
      </Card>
      
      {/* PWA Install Prompt - Appears below login card */}
      <AuthInstallPrompt />
      </div>
    </div>
    </>
  );
};

export default Auth;
